import type { AlertColor } from "@mui/material/Alert/Alert";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { Route, Routes } from "react-router-dom";

import { useStudioFieldDefinitions, useStudioTemplates } from "./hooks";
import StudioBuilder from "./StudioBuilder";
import StudioListView from "./StudioListView";

interface StudioDashboardProps {
  selectedPractices: PracticeMessage[];
  supportedSources: SourceMessage[];
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
}

const NO_TEMPLATES: StudioTemplateMessage[] = [];
const NO_FIELD_DEFINITIONS: StudioFieldDefinitionsMessage = {};

function loadErrorMessage(error: unknown, fallback: string): string {
  return error instanceof AxiosError &&
    typeof error.response?.data?.detail === "string"
    ? error.response.data.detail
    : fallback;
}

export default function StudioDashboard({
  selectedPractices,
  supportedSources,
  updateSnackBar,
}: StudioDashboardProps) {
  const [currentSession, setCurrentSession] =
    useState<StudioSessionMessage | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const templatesQuery = useStudioTemplates();
  const fieldDefinitionsQuery = useStudioFieldDefinitions();

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding updateSnackBar or the error would re-fire the snackbar
  useEffect(() => {
    if (templatesQuery.isError) {
      updateSnackBar({
        severity: "error",
        message: loadErrorMessage(
          templatesQuery.error,
          "Failed to load Studio templates.",
        ),
      });
    }
  }, [templatesQuery.isError]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding updateSnackBar or the error would re-fire the snackbar
  useEffect(() => {
    if (fieldDefinitionsQuery.isError) {
      updateSnackBar({
        severity: "error",
        message: loadErrorMessage(
          fieldDefinitionsQuery.error,
          "Failed to load Studio field definitions.",
        ),
      });
    }
  }, [fieldDefinitionsQuery.isError]);

  const templates = templatesQuery.data ?? NO_TEMPLATES;

  return (
    <Routes>
      <Route
        path="/"
        element={
          <StudioListView
            templates={templates}
            loadedTemplates={!templatesQuery.isPending}
          />
        }
      />
      <Route
        path="/new"
        element={
          <StudioBuilder
            session={currentSession}
            setSession={setCurrentSession}
            isExtracting={isExtracting}
            setIsExtracting={setIsExtracting}
            templates={templates}
            fieldDefinitions={
              fieldDefinitionsQuery.data ?? NO_FIELD_DEFINITIONS
            }
            selectedPractices={selectedPractices}
            supportedSources={supportedSources}
            updateSnackBar={updateSnackBar}
          />
        }
      />
    </Routes>
  );
}
